// src/components/ModeSpecificDocumentSelector.jsx
import React, { useState, useEffect } from 'react';
import {
  EuiPanel,
  EuiTitle,
  EuiCheckboxGroup,
  EuiButton,
  EuiSpacer,
  EuiCallOut,
  EuiLoadingSpinner,
  EuiText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiBadge,
  EuiIcon
} from '@elastic/eui';
import { getApiUrl } from '../utils/api';

/**
 * Component for teachers to configure mode-specific RAG documents
 * Allows different document sets for study guide vs practice questions modes
 */
const ModeSpecificDocumentSelector = ({ course, agentConfig, onConfigUpdate }) => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [studyGuideDocIds, setStudyGuideDocIds] = useState([]);
  const [practiceDocIds, setPracticeDocIds] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agentConfig || !course) return;
    
    loadConfiguration();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentConfig, course]);

  const loadConfiguration = async () => {
    setLoading(true);
    
    try {
      // Combine all uploaded documents
      const docs = [
        ...(agentConfig.required_inclusion_documents || []),
        ...(agentConfig.non_required_inclusion_documents || [])
      ];
      
      setAllDocuments(docs);
      
      // Load current mode-specific selections
      await loadModeDocuments('study_guide');
      await loadModeDocuments('practice_questions');
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModeDocuments = async (modeType) => {
    try {
      const response = await fetch(getApiUrl('/api/agent/mode-documents/get'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          course_name: course.course_name,
          config_name: agentConfig.config_name,
          mode_type: modeType
        })
      });
      
      if (!response.ok) {
        console.error(`Failed to load ${modeType} documents`);
        return;
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        const docNames = data.documents.map(d => d.name);
        if (modeType === 'study_guide') {
          setStudyGuideDocIds(docNames);
        } else {
          setPracticeDocIds(docNames);
        }
      }
    } catch (error) {
      console.error(`Error loading ${modeType} documents:`, error);
    }
  };

  const saveConfiguration = async (modeType, selectedDocNames) => {
    setSaveStatus({ loading: true, type: modeType });
    
    try {
      const response = await fetch(getApiUrl('/api/agent/mode-documents/update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          course_name: course.course_name,
          config_name: agentConfig.config_name,
          mode_type: modeType,
          document_names: selectedDocNames
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        data = { status: 'error', message: 'Unexpected response from server.' };
      }
      
      if (response.ok && data.status === 'success') {
        setSaveStatus({ 
          success: true, 
          type: modeType,
          message: data.message 
        });
        
        // Call parent component callback if provided
        if (onConfigUpdate) {
          onConfigUpdate();
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      } else {
        setSaveStatus({ 
          error: true, 
          type: modeType,
          message: data.message || 'Failed to save configuration'
        });
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus({ 
        error: true, 
        type: modeType,
        message: 'Network error: Failed to save configuration'
      });
    }
  };

  if (loading) {
    return (
      <EuiFlexGroup justifyContent="center" alignItems="center">
        <EuiFlexItem grow={false}>
          <EuiLoadingSpinner size="l" />
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <EuiCallOut
        title="No documents uploaded"
        color="warning"
        iconType="alert"
      >
        <p>
          Please upload documents to this agent configuration before configuring mode-specific settings.
        </p>
      </EuiCallOut>
    );
  }

  const checkboxOptions = allDocuments.map(doc => ({
    id: doc.name,
    label: (
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon type="document" />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiText size="s">
            <strong>{doc.name}</strong>
            {doc.description && (
              <span style={{ color: '#69707D', marginLeft: '8px' }}>
                {doc.description}
              </span>
            )}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }));

  return (
    <>
      <EuiSpacer size="l" />
      
      <EuiCallOut
        title="Configure Mode-Specific Documents"
        color="primary"
        iconType="iInCircle"
      >
        <p>
          Choose different documents for study sessions vs practice sessions. 
          This allows the AI to provide contextually appropriate help based on what students are doing.
        </p>
      </EuiCallOut>
      
      <EuiSpacer size="l" />
      
      {/* Study Guide Mode Panel */}
      <EuiPanel hasBorder>
        <EuiFlexGroup alignItems="center" gutterSize="m">
          <EuiFlexItem grow={false}>
            <EuiIcon type="book" size="l" color="primary" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiTitle size="s">
              <h3>Study Guide Mode Documents</h3>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiBadge color="primary">
              {studyGuideDocIds.length} selected
            </EuiBadge>
          </EuiFlexItem>
        </EuiFlexGroup>
        
        <EuiSpacer size="s" />
        
        <EuiText size="s" color="subdued">
          Students in study/review sessions will use these materials for conceptual learning and understanding.
        </EuiText>
        
        <EuiSpacer size="m" />
        
        <EuiCheckboxGroup
          options={checkboxOptions}
          idToSelectedMap={Object.fromEntries(
            studyGuideDocIds.map(id => [id, true])
          )}
          onChange={(id) => {
            setStudyGuideDocIds(prev =>
              prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
            );
          }}
        />
        
        <EuiSpacer size="m" />
        
        <EuiFlexGroup gutterSize="s">
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              iconType="save"
              onClick={() => saveConfiguration('study_guide', studyGuideDocIds)}
              isLoading={saveStatus?.loading && saveStatus?.type === 'study_guide'}
            >
              Save Study Guide Configuration
            </EuiButton>
          </EuiFlexItem>
          
          {studyGuideDocIds.length === 0 && (
            <EuiFlexItem grow={false}>
              <EuiText size="s" color="warning">
                <EuiIcon type="alert" /> No documents selected - will use default documents
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        
        {saveStatus?.type === 'study_guide' && saveStatus?.success && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut 
              title="Configuration saved successfully" 
              color="success" 
              iconType="check"
              size="s"
            />
          </>
        )}
        
        {saveStatus?.type === 'study_guide' && saveStatus?.error && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut 
              title="Failed to save configuration" 
              color="danger" 
              iconType="alert"
              size="s"
            >
              <p>{saveStatus.message}</p>
            </EuiCallOut>
          </>
        )}
      </EuiPanel>
      
      <EuiSpacer size="l" />
      
      {/* Practice Questions Mode Panel */}
      <EuiPanel hasBorder>
        <EuiFlexGroup alignItems="center" gutterSize="m">
          <EuiFlexItem grow={false}>
            <EuiIcon type="pencil" size="l" color="success" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiTitle size="s">
              <h3>Practice Questions Mode Documents</h3>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiBadge color="success">
              {practiceDocIds.length} selected
            </EuiBadge>
          </EuiFlexItem>
        </EuiFlexGroup>
        
        <EuiSpacer size="s" />
        
        <EuiText size="s" color="subdued">
          Students in practice sessions will use these materials for problem-solving and hands-on practice.
        </EuiText>
        
        <EuiSpacer size="m" />
        
        <EuiCheckboxGroup
          options={checkboxOptions}
          idToSelectedMap={Object.fromEntries(
            practiceDocIds.map(id => [id, true])
          )}
          onChange={(id) => {
            setPracticeDocIds(prev =>
              prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
            );
          }}
        />
        
        <EuiSpacer size="m" />
        
        <EuiFlexGroup gutterSize="s">
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              iconType="save"
              color="success"
              onClick={() => saveConfiguration('practice_questions', practiceDocIds)}
              isLoading={saveStatus?.loading && saveStatus?.type === 'practice_questions'}
            >
              Save Practice Configuration
            </EuiButton>
          </EuiFlexItem>
          
          {practiceDocIds.length === 0 && (
            <EuiFlexItem grow={false}>
              <EuiText size="s" color="warning">
                <EuiIcon type="alert" /> No documents selected - will use default documents
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        
        {saveStatus?.type === 'practice_questions' && saveStatus?.success && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut 
              title="Configuration saved successfully" 
              color="success" 
              iconType="check"
              size="s"
            />
          </>
        )}
        
        {saveStatus?.type === 'practice_questions' && saveStatus?.error && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut 
              title="Failed to save configuration" 
              color="danger" 
              iconType="alert"
              size="s"
            >
              <p>{saveStatus.message}</p>
            </EuiCallOut>
          </>
        )}
      </EuiPanel>
      
      <EuiSpacer size="l" />
      
      {/* Info Panel */}
      <EuiCallOut
        title="How This Works"
        iconType="questionInCircle"
        color="primary"
      >
        <ul>
          <li>
            <strong>Study Guide Mode:</strong> When students create a "study" session, 
            the AI will only search the documents you selected above for answers.
          </li>
          <li>
            <strong>Practice Questions Mode:</strong> When students create a "practice" session, 
            the AI will only search the practice-specific documents.
          </li>
          <li>
            <strong>Default Behavior:</strong> If you don't select any documents for a mode, 
            the system will use all uploaded documents (default behavior).
          </li>
          <li>
            <strong>Updates Take Effect Immediately:</strong> Changes apply to new chat messages 
            in existing sessions and all new sessions.
          </li>
        </ul>
      </EuiCallOut>
    </>
  );
};

export default ModeSpecificDocumentSelector;
